import React, { useEffect, useRef, useState, useContext } from 'react'
import ROSLIB from 'roslib'
import Context from '../utils/Context'

function RosCommandTerminal() {
    const [inputCommand, setInputCommand] = useState('')
    const [status, setStatus] = useState('Conectando...')
    const [connected, setConnected] = useState(false)
    const [terminalLines, setTerminalLines] = useState([
        'Command Terminal ROS',
        'Enter example command:: ros2 topic echo /wgo/zone_occupied_detection/warningMsg',
        ''
    ])
    const [selectedCombo, setSelectedCombo] = useState('')
    const [isSubscribed, setIsSubscribed] = useState(false)
    const [pubRate, setPubRate] = useState('')
    const [pubData, setPubData] = useState('')
    const [selectedCommandObj, setSelectedCommandObj] = useState(null)
    const rosRef = useRef(null)
    const subscriberRef = useRef(null)

    const context = useContext(Context)
    const robotIp = context.selectedRobot?.ip
    const rosType = context.selectedRobot?.ROS
    const rosCommands = context.rosCommands || []

    // Filter commands by robot type
    const filteredCommands = rosCommands.filter(cmd => cmd.typeRos === rosType)

    useEffect(() => {
        if (!robotIp) return
        // ROSBridge Connect
        const ros = new ROSLIB.Ros({
            url: `ws://${robotIp}:9090`
        })
        rosRef.current = ros

        ros.on('connection', () => {
            setStatus('Connected to ROSBridge.')
            setConnected(true)
            setTerminalLines(lines => [...lines, '[Connected to ROSBridge]'])
        })
        ros.on('error', (error) => {
            setStatus('Error connecting to ROSBridge')
            setConnected(false)
            setTerminalLines(lines => [...lines, '[Error connecting to ROSBridge]'])
        })
        ros.on('close', () => {
            setStatus('ROSBridge connection closed.')
            setConnected(false)
            setTerminalLines(lines => [...lines, '[ROSBridge connection closed]'])
        })

        return () => {
            if (ros) ros.close()
            if (subscriberRef.current) {
                subscriberRef.current.unsubscribe()
                subscriberRef.current = null
            }
        }
    }, [robotIp])

    // Helper to get correct message type (ROS1/ROS2)
    const getMessageType = (cmdObj, topicName, fallbackType = 'std_msgs/msg/String') => {
        if (!cmdObj) {
            // Attempt to infer message type from topic name
            if (topicName) {
                if (topicName.includes('/scan') || topicName.includes('laser')) {
                    return rosType === 'ROS2' ? 'sensor_msgs/msg/LaserScan' : 'sensor_msgs/LaserScan'
                }
                if (topicName.includes('/odom')) {
                    return rosType === 'ROS2' ? 'nav_msgs/msg/Odometry' : 'nav_msgs/Odometry'
                }
                if (topicName.includes('/cmd_vel')) {
                    return rosType === 'ROS2' ? 'geometry_msgs/msg/Twist' : 'geometry_msgs/Twist'
                }
                if (topicName.includes('TaskFeedback')) {
                    return rosType === 'ROS2' ? 'custom_msgs/msg/TaskFeedback' : 'custom_msgs/TaskFeedback'
                }
                if (topicName.includes('battery') || topicName.includes('Battery')) {
                    return rosType === 'ROS2' ? 'sensor_msgs/msg/BatteryState' : 'sensor_msgs/BatteryState'
                }
                if (topicName === '/tf_static' || topicName === '/tf') {
                    return rosType === 'ROS2' ? 'tf2_msgs/msg/TFMessage' : 'tf2_msgs/TFMessage'
                }
                if (topicName.includes('/clock')) {
                    return rosType === 'ROS2' ? 'rosgraph_msgs/msg/Clock' : 'rosgraph_msgs/Clock'
                }
                if (topicName.includes('/joint_states')) {
                    return rosType === 'ROS2' ? 'sensor_msgs/msg/JointState' : 'sensor_msgs/JointState'
                }
                if (topicName.includes('/map')) {
                    return rosType === 'ROS2' ? 'nav_msgs/msg/OccupancyGrid' : 'nav_msgs/OccupancyGrid'
                }
            }
            return fallbackType
        }
        if (cmdObj.messageType) return cmdObj.messageType
        if (cmdObj.typeRos === 'ROS') return 'std_msgs/String'
        return fallbackType
    }

    const executeRosCommand = (cmd, pubRateValue, pubDataValue) => {
        // First, determine if this is a publish command based on syntax
        const isPublishCommand = cmd.includes('pub') || (selectedCommandObj && selectedCommandObj.needs_value)
        
        if (isPublishCommand) {
            // Handle publish commands
            let topicName, messageType
            
            // Try to parse publish command: ros2 topic pub /topic type or rostopic pub /topic type
            const rosPublishMatch = cmd.match(/ros2?\s+topic\s+pub\s+(?:-r\s+\d+\s+)?(\S+)\s+(\S+)/)
            const rostopicPublishMatch = cmd.match(/rostopic\s+pub\s+(?:-r\s+\d+\s+)?(\S+)\s+(\S+)/)
            
            if (rosPublishMatch) {
                topicName = rosPublishMatch[1]
                messageType = rosPublishMatch[2]
            } else if (rostopicPublishMatch) {
                topicName = rostopicPublishMatch[1]
                messageType = rostopicPublishMatch[2]
            } else if (selectedCommandObj) {
                // Fallback to selected command parsing
                const fallback = cmd.match(/ros2?\s+topic\s+(\S+)\s+(\S+)/)
                topicName = fallback?.[1]
                messageType = fallback?.[2] || getMessageType(selectedCommandObj, topicName)
            }
            
            if (!topicName || !messageType) {
                setTerminalLines(lines => [
                    ...lines,
                    `$ ${cmd}`,
                    '[Invalid publish command. Use: ros2 topic pub /topic_name message_type or rostopic pub /topic_name message_type]'
                ])
                return
            }
            
            // For manual commands, require pub rate and data unless already in command
            if (!selectedCommandObj && (!pubRateValue || !pubDataValue)) {
                setTerminalLines(lines => [
                    ...lines,
                    `$ ${cmd}`,
                    '[For publish commands, fill in pub rate and data fields, or include them in the command]'
                ])
                return
            }
            
            // Stop previous subscription
            if (subscriberRef.current) {
                subscriberRef.current.unsubscribe()
                subscriberRef.current = null
            }
            
            const rate = pubRateValue || '1'
            const data = pubDataValue || 'true'
            
            setTerminalLines(lines => [
                ...lines,
                `$ ${cmd}`,
                `[Publishing to ${topicName} (${messageType}) at ${rate}Hz with data: ${data}]`
            ])
            
            // Publish message
            const topic = new ROSLIB.Topic({
                ros: rosRef.current,
                name: topicName,
                messageType
            })
            
            // Create appropriate message based on type
            let msgData
            if (messageType.includes('Bool')) {
                msgData = { data: data.toLowerCase() === 'true' }
            } else if (messageType.includes('String')) {
                msgData = { data: data }
            } else if (messageType.includes('Float')) {
                msgData = { data: parseFloat(data) || 0.0 }
            } else {
                msgData = { data: data }
            }
            
            const msg = new ROSLIB.Message(msgData)
            topic.publish(msg)
            
            setTerminalLines(lines => [
                ...lines,
                `[Published successfully to ${topicName}]`
            ])
            setIsSubscribed(false)
            return
        } else {
            // Handle echo/subscription commands
            // Support both "ros2 topic echo" and "rostopic echo"
            const ros2Match = cmd.match(/ros2\s+topic\s+echo\s+(\S+)/)
            const ros1Match = cmd.match(/rostopic\s+echo\s+(\S+)/)
            
            if (ros2Match || ros1Match) {
                const topicName = (ros2Match || ros1Match)[1]
                let messageType = getMessageType(selectedCommandObj, topicName)
                
                // Special handling for common topics when no command is selected
                if (!selectedCommandObj) {
                    if (topicName === '/tf_static' || topicName === '/tf') {
                        messageType = rosType === 'ROS2' ? 'tf2_msgs/msg/TFMessage' : 'tf2_msgs/TFMessage'
                    } else if (topicName.includes('/clock')) {
                        messageType = rosType === 'ROS2' ? 'rosgraph_msgs/msg/Clock' : 'rosgraph_msgs/Clock'
                    } else if (topicName.includes('/joint_states')) {
                        messageType = rosType === 'ROS2' ? 'sensor_msgs/msg/JointState' : 'sensor_msgs/JointState'
                    } else if (topicName.includes('/map')) {
                        messageType = rosType === 'ROS2' ? 'nav_msgs/msg/OccupancyGrid' : 'nav_msgs/OccupancyGrid'
                    }
                }
                
                if (subscriberRef.current) {
                    subscriberRef.current.unsubscribe()
                    subscriberRef.current = null
                }
                
                // Configure topic options
                const topicOpts = {
                    ros: rosRef.current,
                    name: topicName,
                    messageType
                }
                
                // For ROS2, use compatible QoS settings
                if (rosType === 'ROS2' || ros2Match) {
                    topicOpts.qos = {
                        durability: 'volatile',
                        reliability: 'best_effort'
                    }
                    
                    // Special QoS for tf_static (typically uses transient_local)
                    if (topicName === '/tf_static') {
                        topicOpts.qos.durability = 'transient_local'
                        topicOpts.qos.reliability = 'reliable'
                    }
                }
                const topic = new ROSLIB.Topic(topicOpts)
                setTerminalLines(lines => [
                    ...lines,
                    `$ ${cmd}`,
                    `[Subscribed to ${topicName} (${messageType}) - waiting for messages...]`,
                    `[DEBUG] QoS: ${JSON.stringify(topicOpts.qos || 'default')}`
                ])
                
                // Add error handling and timeout for debugging
                const timeoutId = setTimeout(() => {
                    setTerminalLines(lines => [
                        ...lines,
                        `[DEBUG] No messages received after 10s. Check if topic exists and publisher is active.`,
                        `[HINT] Try: ros2 topic list | grep ${topicName.split('/').pop()}`
                    ])
                }, 10000)
                
                topic.subscribe((msg) => {
                    clearTimeout(timeoutId)
                    
                    // Format message based on type
                    let displayMsg = ''
                    if (typeof msg.data !== 'undefined') {
                        displayMsg = msg.data
                    } else if (msg.transforms && Array.isArray(msg.transforms)) {
                        // TFMessage formatting (tf_static, tf)
                        displayMsg = `TF: ${msg.transforms.length} transform(s)`
                        if (msg.transforms.length > 0) {
                            const tf = msg.transforms[0]
                            const trans = tf.transform.translation
                            displayMsg += ` | ${tf.child_frame_id} -> ${tf.header.frame_id}: pos(${trans.x.toFixed(3)}, ${trans.y.toFixed(3)}, ${trans.z.toFixed(3)})`
                        }
                    } else if (msg.ranges && msg.ranges.length) {
                        // LaserScan formatting
                        displayMsg = `LaserScan: ${msg.ranges.length} points, range [${Math.min(...msg.ranges).toFixed(2)}, ${Math.max(...msg.ranges).toFixed(2)}]`
                        if (msg.header && msg.header.stamp) {
                            displayMsg += `, timestamp: ${msg.header.stamp.sec}.${msg.header.stamp.nanosec}`
                        }
                    } else if (msg.pose && msg.pose.position) {
                        // Pose formatting
                        const pos = msg.pose.position
                        displayMsg = `Position: x=${pos.x.toFixed(3)}, y=${pos.y.toFixed(3)}, z=${pos.z.toFixed(3)}`
                    } else if (msg.position && Array.isArray(msg.position)) {
                        // JointState formatting
                        displayMsg = `JointState: ${msg.position.length} joints`
                        if (msg.name && msg.name.length > 0) {
                            displayMsg += ` | ${msg.name.slice(0, 3).join(', ')}${msg.name.length > 3 ? '...' : ''}`
                        }
                    } else if (msg.clock && msg.clock.sec !== undefined) {
                        // Clock formatting
                        displayMsg = `Clock: ${msg.clock.sec}.${msg.clock.nanosec || 0}`
                    } else if (msg.twist && msg.twist.linear) {
                        // Twist formatting
                        const lin = msg.twist.linear
                        const ang = msg.twist.angular
                        displayMsg = `Twist: linear(${lin.x.toFixed(3)}, ${lin.y.toFixed(3)}, ${lin.z.toFixed(3)}) angular(${ang.x.toFixed(3)}, ${ang.y.toFixed(3)}, ${ang.z.toFixed(3)})`
                    } else if (msg.voltage !== undefined && msg.percentage !== undefined) {
                        // BatteryState formatting
                        displayMsg = `Battery: ${msg.voltage.toFixed(2)}V, ${msg.percentage.toFixed(1)}%, current: ${msg.current.toFixed(2)}A`
                        if (msg.power_supply_status !== undefined) {
                            const statusMap = {0: 'Unknown', 1: 'Charging', 2: 'Discharging', 3: 'Not charging', 4: 'Full'}
                            displayMsg += `, status: ${statusMap[msg.power_supply_status] || msg.power_supply_status}`
                        }
                    } else {
                        // Generic formatting
                        displayMsg = JSON.stringify(msg).substring(0, 200) + '...'
                    }
                    
                    setTerminalLines(lines => [
                        ...lines,
                        `[${topicName}] ${displayMsg}`
                    ])
                })
                
                // Handle subscription errors
                topic.on('error', (error) => {
                    clearTimeout(timeoutId)
                    setTerminalLines(lines => [
                        ...lines,
                        `[ERROR] Subscription failed: ${error.message || error}`,
                        `[HINT] Check message type: ${messageType}`
                    ])
                })
                
                subscriberRef.current = topic
                setIsSubscribed(true)
                return
            }
            // ROS1: rostopic echo (without space)
            const match1 = cmd.match(/rostopic\s+echo\s+(\S+)/)
            if (match1) {
                const topicName = match1[1]
                const messageType = getMessageType(selectedCommandObj, topicName)
                if (subscriberRef.current) {
                    subscriberRef.current.unsubscribe()
                    subscriberRef.current = null
                }
                const topic = new ROSLIB.Topic({
                    ros: rosRef.current,
                    name: topicName,
                    messageType
                })
                setTerminalLines(lines => [
                    ...lines,
                    `$ ${cmd}`,
                    `[Subscribed to ${topicName} (${messageType}) - waiting for messages...]`
                ])
                topic.subscribe((msg) => {
                    // Format message based on type (same logic as ROS2)
                    let displayMsg = ''
                    if (typeof msg.data !== 'undefined') {
                        displayMsg = msg.data
                    } else if (msg.ranges && msg.ranges.length) {
                        displayMsg = `LaserScan: ${msg.ranges.length} points, range [${Math.min(...msg.ranges).toFixed(2)}, ${Math.max(...msg.ranges).toFixed(2)}]`
                        if (msg.header && msg.header.stamp) {
                            displayMsg += `, timestamp: ${msg.header.stamp.sec}.${msg.header.stamp.nanosec}`
                        }
                    } else if (msg.pose && msg.pose.position) {
                        const pos = msg.pose.position
                        displayMsg = `Position: x=${pos.x.toFixed(3)}, y=${pos.y.toFixed(3)}, z=${pos.z.toFixed(3)}`
                    } else if (msg.twist && msg.twist.linear) {
                        const lin = msg.twist.linear
                        const ang = msg.twist.angular
                        displayMsg = `Twist: linear(${lin.x.toFixed(3)}, ${lin.y.toFixed(3)}, ${lin.z.toFixed(3)}) angular(${ang.x.toFixed(3)}, ${ang.y.toFixed(3)}, ${ang.z.toFixed(3)})`
                    } else if (msg.voltage !== undefined && msg.percentage !== undefined) {
                        // BatteryState formatting
                        displayMsg = `Battery: ${msg.voltage.toFixed(2)}V, ${msg.percentage.toFixed(1)}%, current: ${msg.current.toFixed(2)}A`
                        if (msg.power_supply_status !== undefined) {
                            const statusMap = {0: 'Unknown', 1: 'Charging', 2: 'Discharging', 3: 'Not charging', 4: 'Full'}
                            displayMsg += `, status: ${statusMap[msg.power_supply_status] || msg.power_supply_status}`
                        }
                    } else {
                        displayMsg = JSON.stringify(msg).substring(0, 200) + '...'
                    }
                    
                    setTerminalLines(lines => [
                        ...lines,
                        `[${topicName}] ${displayMsg}`
                    ])
                })
                subscriberRef.current = topic
                setIsSubscribed(true)
                return
            }
            
            // If no pattern matched, show helpful error message
            setTerminalLines(lines => [
                ...lines,
                `$ ${cmd}`,
                '[Unrecognized command format]',
                '[Supported formats:]',
                '[  Echo: "ros2 topic echo /topic_name" or "rostopic echo /topic_name"]',
                '[  Publish: "ros2 topic pub /topic_name message_type" or "rostopic pub /topic_name message_type"]',
                '[  Examples: "rostopic echo /tf_static", "ros2 topic echo /scan"]'
            ])
        }
    }

    const sendCommand = () => {
        if (!connected || !inputCommand.trim()) return
        if (selectedCommandObj && selectedCommandObj.needs_value) {
            if (!pubRate || !pubData) {
                setTerminalLines(lines => [
                    ...lines,
                    '[Fill in pub and data fields to publish.]'
                ])
                return
            }
            executeRosCommand(inputCommand.trim(), pubRate, pubData)
            setPubRate('')
            setPubData('')
        } else {
            executeRosCommand(inputCommand.trim())
        }
        setInputCommand('')
        setSelectedCombo('')
    }

    const stopSubscription = () => {
        if (subscriberRef.current) {
            subscriberRef.current.unsubscribe()
            subscriberRef.current = null
            setTerminalLines(lines => [
                ...lines,
                '[Subscription stopped.]'
            ])
            setIsSubscribed(false)
        }
    }

    const diagnoseTopics = () => {
        if (!connected) return
        
        setTerminalLines(lines => [
            ...lines,
            '[DEBUG] Diagnosing available topics...'
        ])
        
        // Get topics list using rosbridge service call
        const getTopicsService = new ROSLIB.Service({
            ros: rosRef.current,
            name: rosType === 'ROS2' ? '/rosapi/topics' : '/rosapi/topics',
            serviceType: rosType === 'ROS2' ? 'rosapi/GetTopics' : 'rosapi/Topics'
        })
        
        const request = new ROSLIB.ServiceRequest({})
        
        getTopicsService.callService(request, (result) => {
            setTerminalLines(lines => [
                ...lines,
                '[DEBUG] Available topics:',
                ...result.topics.slice(0, 20).map(topic => `  - ${topic}`),
                result.topics.length > 20 ? `  ... and ${result.topics.length - 20} more` : ''
            ])
        }, (error) => {
            setTerminalLines(lines => [
                ...lines,
                `[DEBUG] Failed to get topics list: ${error}`
            ])
        })
    }

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            sendCommand()
        }
    }

    // When selecting a combo command, fills the input and sets the selected object
    const handleComboChange = (e) => {
        const selectedValue = e.target.value
        setSelectedCombo(selectedValue)
        setInputCommand(selectedValue)
        const cmdObj = filteredCommands.find(cmd => cmd.value === selectedValue)
        setSelectedCommandObj(cmdObj || null)
        setPubRate('')
        setPubData('')
    }

    return (
        <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
            <h1>Terminal de Comando ROS</h1>
            <div style={{
                marginBottom: 10,
                color: connected ? 'green' : 'red'
            }}>
                {status}
            </div>
            <div style={{ marginBottom: 15 }}>
                <label style={{ marginRight: 10, fontWeight: 'bold' }}>Quick commands:</label>
                <select
                    value={selectedCombo}
                    onChange={handleComboChange}
                    style={{ minWidth: 300, padding: 6, borderRadius: 4, border: '1px solid #333' }}
                    disabled={!connected || filteredCommands.length === 0}
                >
                    <option value="">Selecione um comando...</option>
                    {filteredCommands.map((cmd, idx) => (
                        <option key={cmd.name || idx} value={cmd.value}>
                            {cmd.name}
                        </option>
                    ))}
                </select>
            </div>
            
            <div style={{
                background: '#181818',
                color: '#d4d4d4',
                fontFamily: 'monospace',
                fontSize: 15,
                borderRadius: 6,
                border: '1px solid #333',
                minHeight: 250,
                maxHeight: 350,
                overflowY: 'auto',
                padding: 12,
                marginBottom: 10
            }}>
                {terminalLines.map((line, idx) => (
                    <div key={idx} style={{ whiteSpace: 'pre-wrap' }}>{line}</div>
                ))}
            </div>
            {selectedCommandObj && selectedCommandObj.needs_value && (
                <div style={{ display: 'flex', marginBottom: 15, gap: 10 }}>
                    <input
                        type="number"
                        min="1"
                        placeholder="pub (rate)"
                        value={pubRate}
                        onChange={e => setPubRate(e.target.value)}
                        style={{
                            width: 100,
                            padding: '8px',
                            border: '1px solid #333',
                            borderRadius: '4px',
                            background: '#222',
                            color: '#fff',
                            fontFamily: 'monospace'
                        }}
                        disabled={!connected}
                    />
                    <input
                        type="text"
                        placeholder="data"
                        value={pubData}
                        onChange={e => setPubData(e.target.value)}
                        style={{
                            flexGrow: 1,
                            padding: '8px',
                            border: '1px solid #333',
                            borderRadius: '4px',
                            background: '#222',
                            color: '#fff',
                            fontFamily: 'monospace'
                        }}
                        disabled={!connected}
                    />
                </div>
            )}
            <div style={{ display: 'flex' }}>
                <span style={{
                    background: '#222',
                    color: '#0f0',
                    fontFamily: 'monospace',
                    padding: '8px 8px 8px 12px',
                    borderRadius: '4px 0 0 4px',
                    border: '1px solid #333',
                    borderRight: 'none'
                }}>$</span>
                <input
                    type="text"
                    value={inputCommand}
                    onChange={(e) => setInputCommand(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder='Ex: rostopic echo /tf_static, ros2 topic echo /scan, ros2 topic pub /test std_msgs/msg/String'
                    style={{
                        flexGrow: 1,
                        padding: '8px',
                        border: '1px solid #333',
                        borderLeft: 'none',
                        borderRadius: '0 4px 4px 0',
                        background: '#222',
                        color: '#fff',
                        fontFamily: 'monospace'
                    }}
                    disabled={!connected}
                    autoFocus
                />
                <button
                    onClick={sendCommand}
                    style={{
                        marginLeft: '10px',
                        padding: '8px 15px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                    disabled={!connected}
                >
                    Send
                </button>
                <button
                    onClick={stopSubscription}
                    style={{
                        marginLeft: '10px',
                        padding: '8px 15px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                    disabled={!isSubscribed}
                >
                    Stop
                </button>
                <button
                    onClick={diagnoseTopics}
                    style={{
                        marginLeft: '10px',
                        padding: '8px 15px',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                    disabled={!connected}
                    title="List available topics for debugging"
                >
                    Debug
                </button>
            </div>
        </div>
    )
}

export default RosCommandTerminal